import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ScreenShare } from 'lucide-react';
import WhatsAppTermsContent from './WhatsAppTermsContent.jsx';
import WhatsAppConnectionModal from './WhatsAppConnectionModal.jsx';

const WhatsApps = lazy(() => import('../pages/WhatsApps.jsx'));

function WhatsAppsModalLauncher({ collapsed = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [modalContainer, setModalContainer] = useState(() => {
    if (typeof document === 'undefined') return null;
    return document.querySelector('.chat-main') || document.body;
  });

  const buttonLabel = 'Conectar WhatsApp';
  const iconOnly = collapsed;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const target = document.querySelector('.chat-main') || document.body;
    setModalContainer(target);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (isTermsModalOpen) {
          setIsTermsModalOpen(false);
          return;
        }
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, isTermsModalOpen]);

  const openModal = useCallback(() => {
    setIsOpen(true);
    setIsTermsModalOpen(false);
    setHasAcceptedTerms(false);
    setIsTermsChecked(false);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setIsTermsModalOpen(false);
    setHasAcceptedTerms(false);
    setIsTermsChecked(false);
  }, []);

  const modalTree = useMemo(() => {
    if (!isOpen) return null;
    return (
      <div className="modal-overlay" onClick={closeModal}>
        <div
          className="modal-content modal-content--connector modal-content--whatsapps"
          onClick={(event) => event.stopPropagation()}
        >
          <WhatsAppConnectionModal
            title="Conectar WhatsApp"
            subtitle="Gerencie suas conexões em um só lugar. Leia e aceite o termo de responsabilidade antes de gerar o QR Code."
            onClose={closeModal}
          >
            <section className="whatsapp-terms-compact" aria-label="Termo de responsabilidade">
              <div className="whatsapp-terms-compact__top">
                <div>
                  <p className="whatsapp-terms-compact__label">Termo de Responsabilidade</p>
                  <p className="whatsapp-terms-compact__text">
                    A leitura e o aceite são obrigatórios para conectar o WhatsApp.
                  </p>
                  {hasAcceptedTerms ? (
                    <p className="whatsapp-terms-compact__accepted">Aceite confirmado para esta sessão.</p>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="whatsapp-terms__link"
                  onClick={() => setIsTermsModalOpen(true)}
                >
                  Ver termo completo
                </button>
              </div>

              {!hasAcceptedTerms ? (
                <label className="whatsapp-terms-compact__consent">
                  <input
                    type="checkbox"
                    checked={isTermsChecked}
                    onChange={(event) => {
                      const { checked } = event.target;
                      setIsTermsChecked(checked);
                      setHasAcceptedTerms(checked);
                    }}
                  />
                  <span>
                    Li e aceito o Termo de Responsabilidade para conectar o meu WhatsApp.
                  </span>
                </label>
              ) : null}
            </section>

            {hasAcceptedTerms ? (
              <div className="whatsapps-embedded-panel">
                <Suspense fallback={<p>Carregando conexões...</p>}>
                  <WhatsApps embedded hideHeader />
                </Suspense>
              </div>
            ) : null}
          </WhatsAppConnectionModal>
        </div>

        {isTermsModalOpen && (
          <div
            className="modal-overlay modal-overlay--nested"
            onClick={(event) => {
              event.stopPropagation();
              setIsTermsModalOpen(false);
            }}
          >
            <div
              className="modal-content modal-content--terms"
              onClick={(event) => event.stopPropagation()}
            >
              <WhatsAppTermsContent />
              <button className="modal-close-button" onClick={() => setIsTermsModalOpen(false)}>
                Fechar
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }, [closeModal, hasAcceptedTerms, isOpen, isTermsChecked, isTermsModalOpen]);

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className={`nav-btn whatsapp-connector ${iconOnly ? 'icon-only' : ''}`}
        data-tooltip={buttonLabel}
        title={iconOnly ? buttonLabel : undefined}
      >
        <ScreenShare size={18} />
        {!iconOnly && <span>{buttonLabel}</span>}
      </button>

      {modalTree && (modalContainer ? createPortal(modalTree, modalContainer) : modalTree)}
    </>
  );
}

export default WhatsAppsModalLauncher;
